import React, { useRef, useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'
import 'highlight.js/styles/github.css'
import '@/styles/highlight-dark.css'
import { useHRChatStreaming } from '@/hooks/useHRChatStreaming'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import useAuthSession from '@/hooks/useAuthSession'
import { HRMessage, MessageSource, HRChatSession } from '@/types/chat.types'
import { StreamingMessage } from '@/components/hr/StreamingMessage'
import { ConnectionStatus, TypingIndicator } from '@/components/hr/ConnectionStatus'
import ConversationItem from '@/components/hr/ConversationItem'
import { updateChatSession, deleteChatSession } from '@/store/slices/hrChatSlice'
import { VersionIndicator } from '@/components/common/VersionIndicator'
import { ChangelogModal } from '@/components/common/ChangelogModal'
import { useAgentVersions } from '@/hooks/useAgentVersions'
import { ChevronDown } from 'lucide-react'

const HRConversationsPage: React.FC = () => {
  // Authentication
  const { user, token } = useAppSelector((state) => state.auth)
  const dispatch = useAppDispatch()
  useAuthSession() // Initialize authentication
  
  // HR chat streaming hook
  const {
    sessions,
    activeSession,
    activeSessionId,
    isInitialized,
    loadingSessions,
    streamingEnabled,
    hasActiveStreams,
    typingUsers,
    isConnected,
    sendMessage,
    cancelMessage,
    loadSession,
    startNewChat,
    sendTyping,
    connect,
    getSessionState
  } = useHRChatStreaming()


  // Local state for input (UI state that doesn't need Redux)
  const [inputValue, setInputValue] = useState('')
  const [showSources, setShowSources] = useState<string | null>(null)
  const [isTyping, setIsTyping] = useState(false)
  const [showChangelog, setShowChangelog] = useState(false)
  
  // Agent versions
  const { versions } = useAgentVersions()
  
  // Debug logging
  useEffect(() => {
    console.log('🏥 HR Page - versions updated:', versions)
  }, [versions])
  
  // Get current session state
  const { messages, isLoading } = getSessionState()
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout>()
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  
  // State for scroll to bottom button
  const [showScrollButton, setShowScrollButton] = useState(false)

  // Smart scroll: only auto-scroll if user is near bottom
  const scrollToBottom = (force = false) => {
    if (!messagesEndRef.current || !messagesContainerRef.current) return
    
    const container = messagesContainerRef.current
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100
    
    if (force || isNearBottom) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  useEffect(() => {
    // Only auto-scroll for new messages, not during streaming updates
    const lastMessage = messages[messages.length - 1]
    if (lastMessage && !lastMessage.isStreaming) {
      scrollToBottom(false) // Smart scroll
    }
  }, [messages.length]) // Only trigger on new messages, not content updates

  // Handle scroll events to show/hide scroll button
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const handleScroll = () => {
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100
      setShowScrollButton(!isNearBottom)
    }

    container.addEventListener('scroll', handleScroll)
    // Initial check
    handleScroll()

    return () => container.removeEventListener('scroll', handleScroll)
  }, [activeSessionId, messages.length]) // Re-check when session or messages change

  // Handle message send
  const handleSendMessage = async () => {
    if (!inputValue.trim() || hasActiveStreams) return

    const messageContent = inputValue.trim()
    setInputValue('')
    setIsTyping(false)
    
    try {
      await sendMessage(messageContent, activeSessionId || undefined)
      // Force scroll to bottom after sending a message
      setTimeout(() => {
        scrollToBottom(true)
        inputRef.current?.focus()
      }, 100)
    } catch (error) {
      console.error('Failed to send message:', error)
      // Restore input value on error
      setInputValue(messageContent)
      inputRef.current?.focus()
    }
  }

  // Handle feedback submission (TODO: Implement with API)
  const handleSubmitFeedback = async (messageId: string, rating: number) => {
    try {
      // TODO: Implement feedback submission via API
      console.log('Feedback submitted:', messageId, rating)
    } catch (error) {
      console.error('Failed to submit feedback:', error)
    }
  }

  // Handle typing indicators
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)

    // Send typing indicator
    if (value.trim() && !isTyping) {
      setIsTyping(true)
      sendTyping(true)
    } else if (!value.trim() && isTyping) {
      setIsTyping(false)
      sendTyping(false)
    }

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }

    // Stop typing after 2 seconds of no input
    if (value.trim()) {
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false)
        sendTyping(false)
      }, 2000)
    }
  }

  // Handle message cancellation
  const handleCancelMessage = (messageId: string) => {
    cancelMessage(messageId)
  }

  // Handle sources toggle
  const toggleSources = (messageId: string | null) => {
    setShowSources(showSources === messageId ? null : messageId)
  }

  // Handle quick action clicks
  const handleQuickAction = (action: string) => {
    const prompts = {
      'leave': "What are our leave policies?",
      'benefits': "What benefits do we offer?",
      'training': "What training programs are available?",
      'compliance': "What are our compliance guidelines?"
    }
    setInputValue(prompts[action as keyof typeof prompts] || action)
    inputRef.current?.focus()
  }

  // Handle Enter key
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  // Handle session rename
  const handleRenameSession = async (sessionId: string, newTitle: string) => {
    try {
      await dispatch(updateChatSession({ sessionId, updates: { title: newTitle } })).unwrap()
    } catch (error) {
      console.error('Failed to rename session:', error)
      throw error
    }
  }

  // Handle session delete
  const handleDeleteSession = async (sessionId: string) => {
    try {
      await dispatch(deleteChatSession(sessionId)).unwrap()
      // If we deleted the active session, start a new chat
      if (sessionId === activeSessionId) {
        startNewChat()
      }
    } catch (error) {
      console.error('Failed to delete session:', error)
      throw error
    }
  }

  // Cleanup typing timeout
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }
  }, [])


  return (
    <div className="px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">HR Conversations</h1>
          <p className="text-muted-foreground">
            Ask questions about HR policies, procedures, and get instant answers
          </p>
        </div>

        {/* Authentication Check */}
        {!user || !token ? (
          <div className="bg-card border rounded-lg p-8 text-center">
            <h3 className="text-lg font-semibold mb-2">Authentication Required</h3>
            <p className="text-muted-foreground">Please log in to access the HR chat system.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Connection Status */}
            <div className="lg:col-span-4">
              <ConnectionStatus 
                onReconnect={connect}
                className="mb-4"
              />
            </div>

            {/* Chat Interface */}
            <div className="lg:col-span-3">
              <div className="bg-card border rounded-lg h-[75vh] min-h-[700px] flex flex-col relative">
                {/* Loading Overlay for Session Switching ONLY */}
                {activeSession?.isLoading && (
                  <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
                    <div className="bg-card border rounded-lg p-6 shadow-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <div>
                          <p className="font-medium">Loading conversation...</p>
                          <p className="text-sm text-muted-foreground">Please wait</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Chat Header */}
                <div className="border-b p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 715.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold flex items-center gap-2">
                          HR Assistant
                          {streamingEnabled && isConnected && (
                            <span className="text-xs bg-green-500/10 text-green-600 dark:bg-green-400/10 dark:text-green-400 px-2 py-1 rounded-full">
                              Live
                            </span>
                          )}
                          {versions.hr && (
                            <VersionIndicator
                              versionInfo={versions.hr}
                              onViewChangelog={() => setShowChangelog(true)}
                              variant="outline"
                              size="sm"
                            />
                          )}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {(() => {
                            if (activeSession?.isLoading) return 'Loading conversation...'
                            if (hasActiveStreams) {
                              return 'Generating response...'
                            }
                            if (activeSessionId) return 'Active conversation'
                            return 'Ready to help with HR questions'
                          })()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={startNewChat}
                      disabled={activeSession?.isLoading}
                      className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-md hover:bg-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      New Chat
                    </button>
                  </div>
                </div>

                {/* Session Error Display */}
                {activeSession?.error && (
                  <div className="p-4 bg-destructive/10 border-b">
                    <p className="text-sm text-destructive">{activeSession.error}</p>
                  </div>
                )}

                {/* Chat Messages */}
                <div ref={messagesContainerRef} className="flex-1 p-6 overflow-y-auto">                  
                  <div className="space-y-6 max-w-4xl mx-auto">
                    {/* Welcome Message */}
                    {messages.length === 0 && (
                      <div className="flex items-start space-x-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div className="bg-muted rounded-lg p-4 max-w-2xl">
                          <p className="text-base mb-3">
                            Hello! I'm your HR Assistant. I can help you with questions about:
                          </p>
                          <ul className="text-base space-y-2 mb-3">
                            <li>• Company policies and procedures</li>
                            <li>• Benefits and compensation</li>
                            <li>• Leave policies and requests</li>
                            <li>• Training and development</li>
                            <li>• Compliance and regulations</li>
                          </ul>
                          <p className="text-base">
                            How can I help you today?
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Actual Messages */}
                    {messages.map((message: HRMessage) => (
                      <div key={message.id} className={`group flex items-start space-x-4 ${message.role === 'USER' ? 'justify-end' : ''}`}>
                        {message.role === 'ASSISTANT' && !message.isStreaming && (
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 715.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                        )}
                        
                        {/* Use StreamingMessage for assistant messages */}
                        {message.role === 'ASSISTANT' ? (
                          <StreamingMessage
                            message={message}
                            onCancelStream={handleCancelMessage}
                            onToggleSources={toggleSources}
                            showSources={showSources === message.id}
                          />
                        ) : (
                          <div className="bg-primary text-primary-foreground rounded-lg p-4 max-w-3xl ml-auto">
                            <div className="prose prose-base max-w-none leading-relaxed">
                              <ReactMarkdown 
                                rehypePlugins={[rehypeHighlight]}
                                components={{
                                  p: ({ children }) => <p className="mb-3 last:mb-0 text-base leading-relaxed">{children}</p>,
                                  ul: ({ children }) => <ul className="mb-3 last:mb-0 ml-4 space-y-1">{children}</ul>,
                                  ol: ({ children }) => <ol className="mb-3 last:mb-0 ml-6 space-y-1 list-decimal list-outside">{children}</ol>,
                                  li: ({ children }) => <li className="text-base leading-relaxed">{children}</li>,
                                  code: ({ children, ...props }) => 
                                    <code className="bg-background/30 px-2 py-1 rounded text-sm font-mono" {...props}>
                                      {children}
                                    </code>,
                                  pre: ({ children }) => 
                                    <pre className="bg-background/30 p-4 rounded-md text-sm overflow-auto my-3 font-mono">
                                      {children}
                                    </pre>,
                                  h1: ({ children }) => <h1 className="text-xl font-bold mb-3 mt-2">{children}</h1>,
                                  h2: ({ children }) => <h2 className="text-lg font-bold mb-2 mt-2">{children}</h2>,
                                  h3: ({ children }) => <h3 className="text-base font-semibold mb-2 mt-2">{children}</h3>,
                                  blockquote: ({ children }) => 
                                    <blockquote className="border-l-4 border-current pl-4 my-3 opacity-80 italic">
                                      {children}
                                    </blockquote>,
                                  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                                  em: ({ children }) => <em className="italic">{children}</em>,
                                  hr: () => <hr className="my-4 border-current opacity-30" />
                                }}
                              >
                                {message.content}
                              </ReactMarkdown>
                            </div>
                          </div>
                        )}

                        {message.role === 'USER' && (
                          <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-base font-medium">{user?.firstName?.[0] || 'U'}</span>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Typing Indicator */}
                    {typingUsers.length > 0 && (
                      <TypingIndicator typingUsers={typingUsers} />
                    )}

                    {/* Loading Indicator - only show for non-streaming fallback */}
                    {!streamingEnabled && hasActiveStreams && (
                      <div className="flex items-start space-x-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        </div>
                        <div className="bg-muted rounded-lg p-4">
                          <p className="text-base text-muted-foreground">Thinking...</p>
                        </div>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {/* Scroll to Bottom Button - positioned like ChatGPT */}
                {showScrollButton && (
                  <div className="relative">
                    <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-10">
                      <button
                        onClick={() => scrollToBottom(true)}
                        className="bg-background border border-border rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                        aria-label="Scroll to bottom"
                      >
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Chat Input */}
                <div className="border-t p-6">
                  <div className="max-w-4xl mx-auto">
                    <div className="flex space-x-3">
                      <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyPress}
                        placeholder="Ask a question about HR policies..."
                        disabled={hasActiveStreams}
                        className="flex-1 px-4 py-3 text-base bg-background text-foreground border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 placeholder:text-muted-foreground"
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={!inputValue.trim() || hasActiveStreams}
                        className="bg-primary text-primary-foreground px-6 py-3 text-base font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {(() => {
                          if (hasActiveStreams) {
                            return 'Generating...'
                          }
                          return 'Send'
                        })()}
                      </button>
                    </div>
                    <div className="flex items-center justify-end mt-3">
                      <span className="text-sm text-muted-foreground">Press Enter to send</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <div className="bg-card border rounded-lg p-4">
                <h3 className="font-semibold mb-3">Quick Actions</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => handleQuickAction('leave')}
                    className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                  >
                    View Leave Policies
                  </button>
                  <button
                    onClick={() => handleQuickAction('benefits')}
                    className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                  >
                    Benefits Overview
                  </button>
                  <button
                    onClick={() => handleQuickAction('training')}
                    className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                  >
                    Training Programs
                  </button>
                  <button
                    onClick={() => handleQuickAction('compliance')}
                    className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
                  >
                    Compliance Guidelines
                  </button>
                </div>
              </div>

              {/* Recent Sessions */}
              <div className="bg-card border rounded-lg p-4">
                <h3 className="font-semibold mb-3">Recent Conversations</h3>
                <div className="space-y-2">
                  {loadingSessions ? (
                    <div className="text-sm text-muted-foreground">Loading...</div>
                  ) : sessions.length > 0 ? (
                    sessions.map((session: HRChatSession) => (
                      <ConversationItem
                        key={session.id}
                        session={session}
                        isActive={activeSessionId === session.id}
                        isLoading={session.isLoading}
                        onSelect={loadSession}
                        onRename={handleRenameSession}
                        onDelete={handleDeleteSession}
                      />
                    ))
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      No recent conversations
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Changelog Modal */}
        <ChangelogModal
          isOpen={showChangelog}
          onClose={() => setShowChangelog(false)}
          agentType="HR"
        />
      </div>
    </div>
  )
}

export default HRConversationsPage